import { Accordion, AccordionDetails, AccordionSummary, Grid, GridSize } from '@mui/material';
import cx from 'classnames';
import {
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { concat, map, toInteger } from 'lodash';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import {
  RESOURCE_NAME_BY_TYPE,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { getDateString } from 'js/lib/DateUtils';
import { bytesToString, formatNumber } from 'js/lib/FormatUtils';
import Button from '../../elements/editors/mui/Button';
import Tooltip from '../../elements/mui/Tooltip';
import { shouldHideRecordsForResource } from '../../../lib/PipelineUtils';
import { ROUTE_FLOWS } from 'new/constants/pathnames';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { getResourceEntityKey } from 'new/hooks/adapters/useResourceMeta';
import useQueryParams from 'new/hooks/useQueryParams';

import s from './FlowRunSummary.module.scss';

interface ResourceMetricEntry {
  id: number;
  metric: { records: number; errors: number; size: number };
}

export interface RunSummary {
  data_sources?: ResourceMetricEntry[];
  data_sets?: ResourceMetricEntry[];
  data_sinks?: ResourceMetricEntry[];
  // Flow-level rollup from metrics-http, same {id, metric} shape - at most one
  // entry, since this endpoint is scoped to one flow.
  flow?: ResourceMetricEntry[];
}

interface Props {
  data: any;
  runId: string;
  index: number;
  summary: RunSummary;
  isExpanded: boolean;
  onClickViewLogs: () => void;
  onExpanded: (index: number, isExpanded: boolean) => void;
}

interface HeaderStat {
  value: string;
  description: string;
}

interface HeaderStats {
  records: HeaderStat;
  errors: HeaderStat;
  size: HeaderStat;
  className: string;
}

// `flow` missing means the rollup feature is off (metrics-http's flag): show the
// pre-rollup display (the first source's own metric) so turning the flag off never
// degrades today's view. `flow` present but empty means the feature is on and
// nothing was computable: "Unavailable", never a bare 0.
export const getHeaderStats = (summary: RunSummary): HeaderStats => {
  if (!summary.flow) {
    const metric = summary.data_sources?.[0]?.metric;
    return {
      records: {
        value: formatNumber(metric?.records ?? 0),
        description: 'Number of Records ingested by the Source',
      },
      errors: {
        value: formatNumber(metric?.errors ?? 0),
        description: 'Number of Errors raised by the Source',
      },
      size: {
        value: bytesToString(metric?.size ?? 0),
        description: 'Volume of the data ingested',
      },
      className: '',
    };
  }

  const metric = summary.flow[0]?.metric;
  const show = (value: number | undefined, formatter: (value: number) => string) =>
    value === undefined ? 'Unavailable' : formatter(value);
  return {
    records: {
      value: show(metric?.records, formatNumber),
      description: 'Number of Records processed by the flow in this run',
    },
    errors: {
      value: show(metric?.errors, formatNumber),
      description: 'Number of Errors raised across the flow in this run',
    },
    size: {
      value: show(metric?.size, bytesToString),
      description: 'Volume of data processed by the flow in this run',
    },
    className: metric ? '' : s.rollupUnavailable,
  };
};

const FlowRunSummary = ({
  data,
  runId,
  index,
  summary,
  isExpanded,
  onExpanded,
  onClickViewLogs,
}: Props) => {
  const { data_sources, data_sets, data_sinks } = summary;
  const stats = getHeaderStats(summary);

  const metricsWithResourceType = (metrics, resourceType: string) =>
    map(metrics, (data) => ({ ...data, resourceType }));
  const allMetrics = concat(
    metricsWithResourceType(data_sources, RESOURCE_TYPE_SOURCE),
    metricsWithResourceType(data_sets, RESOURCE_TYPE_DATA_SET),
    metricsWithResourceType(data_sinks, RESOURCE_TYPE_SINK),
  );

  // Gate the header's Run ID/stats row the same way it always has: on the run having
  // a source metric. The numbers themselves come from getHeaderStats.
  const hasSourceMetric = Boolean(data_sources?.[0]?.metric);

  // check if we hide records for this flow type
  const hideRecords = shouldHideRecordsForResource(data);

  const onAccordionExpanded = (event, isExpanded) => {
    onExpanded(index, isExpanded);
  };

  return (
    <Accordion
      className="accordion-item"
      expanded={isExpanded}
      TransitionProps={{ unmountOnExit: true }}
      onChange={onAccordionExpanded}
    >
      <AccordionSummary
        className="accordion-summary"
        expandIcon={<ExpandMoreIcon fontSize="large" />}
      >
        <Grid className="accordion-summary-content" container spacing={4}>
          <Grid item xs={3} className={s.runIdAsLabel}>
            {getDateString(new Date(toInteger(runId)))}
          </Grid>

          {hasSourceMetric && (
            <>
              <Grid item xs={2}>
                <div className={s.runIdLabel}>Run ID</div>
                <button
                  type="button"
                  className={s.runIdValue}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickViewLogs();
                  }}
                >
                  {runId}
                </button>
              </Grid>
              {!hideRecords && (
                <AccordionSummaryStat
                  name="Records"
                  description={stats.records.description}
                  value={stats.records.value}
                  classNameValue={stats.className}
                  xs={2}
                />
              )}
              <AccordionSummaryStat
                name="Errors"
                description={stats.errors.description}
                value={stats.errors.value}
                classNameValue={stats.className}
              />
              <AccordionSummaryStat
                name="Volume"
                description={stats.size.description}
                value={stats.size.value}
                classNameValue={stats.className}
              />
              <AccordionSummaryStat name="Nexsets" value={data_sets?.length || 0} />
              <AccordionSummaryStat name="Destinations" value={data_sinks?.length || 0} />
            </>
          )}
        </Grid>
      </AccordionSummary>

      <AccordionDetails className="flow-run-summary-details">
        <div className="flow-run-summary-table">
          <Grid className="header-row" container>
            <Grid item xs={2}>
              Resources
            </Grid>
            <Grid item xs={4}>
              Name
            </Grid>
            {!hideRecords && (
              <Grid item xs={2}>
                Records
              </Grid>
            )}
            <Grid item xs={2}>
              Errors
            </Grid>
            <Grid item xs={2}>
              Volume
            </Grid>
          </Grid>
          {map(allMetrics, ({ id, resourceType, metric }, index) => (
            <Grid key={index} className="body-row" container>
              <Grid item xs={2}>
                {RESOURCE_NAME_BY_TYPE[resourceType]}
              </Grid>
              <Grid item xs={4}>
                <RunResourceName
                  resourceType={resourceType}
                  id={id}
                  flowId={data?.origin_node_id}
                />
              </Grid>
              {!hideRecords && (
                <Grid item xs={2}>
                  {formatNumber(metric.records)}
                </Grid>
              )}
              <Grid item xs={2}>
                {formatNumber(metric.errors)}
              </Grid>
              <Grid item xs={2}>
                {bytesToString(metric.size)}
              </Grid>
            </Grid>
          ))}
        </div>

        <div className="view-logs-btn-container">
          <Button variant="outlined" onClick={onClickViewLogs}>
            View Logs
          </Button>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

interface RunResourceNameProps {
  resourceType: string;
  id: number;
  flowId?: number;
}

// Name (plus its id) from the already-loaded flow entities, linking to the flow with
// that node's detail panel open. "Deleted" only when the flow is loaded and the
// resource isn't in it; if the flow isn't in the store, show the id as before.
const RunResourceName = ({ resourceType, id, flowId }: RunResourceNameProps) => {
  const entity = useSelector(
    (state: any) => state.entities?.[`${getResourceEntityKey(resourceType)}s`]?.[id],
  );
  const isFlowLoaded = useSelector(
    (state: any) => flowId != null && !!state.entities?.flowNodes?.[flowId],
  );
  const { embedded, mode } = useQueryParams();
  const typeLabel = RESOURCE_NAME_BY_TYPE[resourceType];
  const idChip = (
    <ChipWithCopy tooltipTitle={`Copy ${typeLabel} ID`} toCopy={String(id)}>
      ID {id}
    </ChipWithCopy>
  );

  if (!entity) {
    return isFlowLoaded ? (
      <span className={s.deletedResource}>{`Deleted ${typeLabel.toLowerCase()} #${id}`}</span>
    ) : (
      idChip
    );
  }

  const name = entity.name || `${typeLabel} #${id}`;
  // The new tab has to stay embedded (and keep its theme) when this page is.
  const params = new URLSearchParams();
  if (entity.flow_node_id != null) params.set('select', String(entity.flow_node_id));
  if (embedded === 'true') {
    params.set('embedded', embedded);
    if (mode) params.set('mode', mode);
  }
  const query = params.toString();
  return (
    <span className={s.resourceCell}>
      {flowId == null ? (
        <span className={s.resourceName} title={name}>
          {name}
        </span>
      ) : (
        <a
          className={cx(s.resourceName, s.resourceLink)}
          href={`${ROUTE_FLOWS}/${flowId}${query ? `?${query}` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${name}, opens the flow in a new tab`}
          title={name}
        >
          {name}
        </a>
      )}
      <span className={s.resourceId}>{idChip}</span>
    </span>
  );
};

interface AccordionSummaryStatProps {
  name: string;
  description?: string;
  value: any;
  xs?: GridSize;
  onClickValue?: () => void;
  classNameValue?: string;
}

const AccordionSummaryStat = ({
  name,
  description = '',
  value,
  xs = 1,
  onClickValue,
  classNameValue = '',
}: AccordionSummaryStatProps) => (
  <Grid className="stat" item xs={xs}>
    <div className="stat-name">
      {name}
      {description && (
        // @ts-ignore
        <Tooltip className="info-icon" title={`${description}: ${value}`} arrow placement="top">
          <InfoOutlinedIcon />
        </Tooltip>
      )}
    </div>
    <div onClick={onClickValue} className={cx('stat-value', classNameValue)}>
      {value}
    </div>
  </Grid>
);

export default memo(FlowRunSummary);
